import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  OutlinedInput,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { UserContext } from '../../../../../platform/components/auth/auth-context';
import { useSelectedBusiness } from '../../../../../platform/redux/store';
import { ADD_BOOKING_EXCEPTION, REMOVE_BOOKINGS, SEND_EMAIL_MUTATION } from '../../../../../platform/graphql/mutations';
import { expandBookings } from '../../../../../platform/util/booking/expandBookings';
import { useCalendar } from '../calendar/calendarContext';
import {
  getBookingActionHtmlBlock,
  getBookingActionLinks,
  getBookingActionTextBlock,
} from '../../../util/bookingEmailActions';

const CancelSchedule = ({
  currentData,   
  showCancelModal,  // Modal visibility state passed from the parent
  setShowCancelModal,  // Function to close the modal 
  answersData,
}) => {
  const { bookingDatas, setBookingDatas, refetchBookings } = useCalendar();
  const [message, setMessage] = useState("");
  const [cancelOption, setCancelOption] = useState("single"); // default option for recurring bookings
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser; 
  const { dateTimeLabel } = useDateTimeDisplay();
  const selectedBusiness = useSelectedBusiness(); 



  const [sendEmailMutation] = useMutation(SEND_EMAIL_MUTATION);
  const [removeBookingMutation] = useMutation(REMOVE_BOOKINGS, {
    update(cache, { data: { removeBooking } }) {
      // Evict the deleted booking from the cache and run garbage collection.
      const normalizedId = cache.identify(removeBooking);
      cache.evict({ id: normalizedId });
      cache.gc();
    },
  });

  const [addBookingExceptionMutation] = useMutation(ADD_BOOKING_EXCEPTION);

  const formatMeetingTime = rawTime => dateTimeLabel(rawTime, providerUser?.timeZone?.timezone);


  const handleRefetch = async () => {
    try {
      // Call refetch to get the new data
      await refetchBookings({ fetchPolicy: 'network-only' }); 
    } catch (error) {
      console.error("Error during refetching bookings:", error);
    }
  };

  const answers = answersData?.getBookingAnswers;

  const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

  const extractEmail = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const match = raw.match(EMAIL_RE);
    return match?.[0] || "";
  };

  const findConciergeEmailForBooking = (booking) => {
    if (!booking) return "";
    const bookingId = String(booking?.id || "").trim();
    const mirrorBookingId = `coord:${bookingId}`;
    const mirrorBooking = [].concat(bookingDatas || []).flat().find(
      (b) => String(b?.bookingId || "").trim() === mirrorBookingId
    );
    const directMirrorEmail = extractEmail(mirrorBooking?.inviteeEmail);
    if (directMirrorEmail) return directMirrorEmail;

    const notes = String(mirrorBooking?.quickNotes || "").trim();
    const notesEmail = extractEmail(notes);
    if (notesEmail) return notesEmail;

    return "";
  };

  const findEmailByBookingId = (bookingId) => {
    const directBooking = [].concat(bookingDatas || []).flat().find(
      (b) => b?.id !== undefined && Number(b.id) === Number(bookingId)
    );
    const directInvitee = extractEmail(directBooking?.inviteeEmail);
    if (directInvitee) return directInvitee;

    const safeBookingData = [].concat(bookingDatas || []).flat();
    const targetId = Number(bookingId);
    if (isNaN(targetId)) return null;
    const booking = safeBookingData.find(b =>
      b?.id !== undefined && Number(b.id) === targetId
    );
    if (!booking) return null;
    const answerQuestionId = answers?.find(answer =>
      Number(answer?.bookingId) === Number(booking?.id)
    )?.id;
    if (!answerQuestionId) return null;
    const bAnswers = (Array.isArray(answers) ? answers : [])
      .filter(answer =>
        Number(answer?.bookingId) === targetId
    );
    const groupedAnswers = bAnswers.reduce((acc, cur) => {
      const aId = cur?.id;
      if (aId !== undefined) {
        acc[aId] = acc[aId] || [];
        acc[aId].push(cur?.answer || '');
      }
      return acc;
    }, {});
    const joined = groupedAnswers[answerQuestionId]?.join(", ") || "";
    return extractEmail(joined) || null;
  };

  // Determine if the booking is recurring based on the first item in currentData.
  const isRecurring =
    currentData &&
    currentData.length > 0 &&
    currentData[0].isRecurring  

  const normalizedCurrentBookings = Array.isArray(currentData)
    ? currentData.filter(Boolean)
    : currentData ? [currentData] : [];
  const cancelPreviewItems = normalizedCurrentBookings.slice(0, 3).map((booking) => ({
    id: booking?.id,
    title: booking?.bookingTypeName || booking?.quickTitle || "Booking",
    time: formatMeetingTime(booking?.time),
    invitee: String(booking?.inviteeName || "").trim(),
  }));

  // Define a cancellation message.
  const cancellationMessage = "Please confirm that you would like to cancel this booking. A cancellation email will be sent to all involved parties.";

  // Handle the cancellation logic and email sending.
  const sendCancellationEmail = async (booking, reason, { singleInstance = false } = {}) => {
    const attendeeEmail = findEmailByBookingId(booking?.id);
    const fallbackInviteeEmail = extractEmail(booking?.inviteeEmail);
    const toEmail = attendeeEmail || fallbackInviteeEmail;
    const fromEmail = String(booking?.staffEmail || providerUser?.email || "").trim();
    if (!toEmail || !fromEmail) {
      return false;
    }

    const meetingTime = formatMeetingTime(booking.time);
    const scopeText = singleInstance ? "occurrence" : "meeting";
    const bookingActionLinks = getBookingActionLinks({
      origin: window.location.origin,
      bookingId: booking?.id,
      timeValue: booking?.time,
      email: toEmail,
    });
    const emailBody = `
      <p>Hello,</p>
      <p>
        Your <strong>${booking.bookingTypeName}</strong> ${scopeText}
        with <strong>${providerUser?.username || "the provider"}</strong>, scheduled for
        <strong>${meetingTime}</strong>, has been cancelled.
      </p>
      ${reason ? `<p>Note: "${reason}"</p>` : ""}
      ${getBookingActionHtmlBlock({
        ...bookingActionLinks,
        intro: "If you want to review the cancelled booking or book again later, use the options below.",
      })}
      <p>
        If you have any questions or would like to reschedule, please contact us.
      </p>
      <p>Best regards,</p>
    `;

    await sendEmailMutation({
      variables: {
        bookingId: String(booking.id),
        from_email: fromEmail,
        to: toEmail,
        subject: "Meeting Cancellation Notification",
        text: `Hello,
Your ${booking.bookingTypeName} ${scopeText} with ${providerUser?.username || "the provider"}, scheduled for ${meetingTime}, has been cancelled.
${reason ? `Note: "${reason}"` : ""}
${getBookingActionTextBlock(bookingActionLinks)}
If you have any questions or would like to reschedule, please contact the provider.
Best regards,`,
        html: emailBody,
      },
    });

    const conciergeEmail = findConciergeEmailForBooking(booking);
    if (conciergeEmail && conciergeEmail.toLowerCase() !== String(toEmail).toLowerCase()) {
      await sendEmailMutation({
        variables: {
          bookingId: String(booking.id),
        from_email: fromEmail,
          to: conciergeEmail,
          subject: "Meeting Cancellation Notification",
          text: `Hello,
The ${booking.bookingTypeName} ${scopeText} for ${meetingTime} has been cancelled.
Referrer: ${booking?.inviteeName || "Unknown"}
${reason ? `Note: "${reason}"` : ""}
Best regards,`,
          html: `
            <p>Hello,</p>
            <p>
              The <strong>${booking.bookingTypeName}</strong> ${scopeText} scheduled for
              <strong>${meetingTime}</strong> has been cancelled.
            </p>
            <p><strong>Referrer:</strong> ${booking?.inviteeName || "Unknown"}</p>
            ${reason ? `<p>Note: "${reason}"</p>` : ""}
            <p>Best regards,</p>
          `,
        },
      });
    }
    return true;
  };

  const handleBookingCancellation = async (bookings, message) => {
    const normalizedBookings = Array.isArray(bookings)
      ? bookings.filter(Boolean)
      : bookings ? [bookings] : [];
    if (normalizedBookings.length === 0) {
      console.error("No valid bookings to cancel");
      return;
    }

    if (isRecurring) {
      if (cancelOption === "series") {
    // Cancel entire series: mark each booking as cancelled.
        for (const booking of normalizedBookings) {
          if (!booking?.id) {
            console.error("Invalid booking object:", booking);
            continue;
          }
          try {
            await removeBookingMutation({
              variables: {
                bookingId: parseInt(booking.id, 10),
              },
            });
            await sendCancellationEmail(booking, message, { singleInstance: false });
          } catch (error) {
            console.error(`Error processing booking ${booking.id}:`, error);
          }
        }
        handleRefetch();
      } else {
        // Cancel single instance: add exception for this occurrence.
        for (const booking of normalizedBookings) {
          const occurrenceDate = booking.time; // the occurrence to cancel.
          try {
            await addBookingExceptionMutation({
              variables: {
                input: {
                  bookingId: parseInt(booking.id, 10),
                  exceptionDate: occurrenceDate,
                  reason: message,
                },
              },
            });
            // Keep behavior consistent with modal copy: send cancellation notice for this occurrence.
            await sendCancellationEmail(booking, message, { singleInstance: true });
          } catch (error) {
            console.error(`Error processing exception for booking ${booking.id}:`, error);
          }
        }
        handleRefetch();
      }
    } else {
      // Non-recurring booking cancellation.
      for (const booking of normalizedBookings) {
        if (!booking?.id) {
          console.error("Invalid booking object:", booking);
          continue;
        }
        try {
          await removeBookingMutation({
            variables: {
              bookingId: parseInt(booking.id, 10),
            },
          });
          await sendCancellationEmail(booking, message, { singleInstance: false });
        } catch (error) {
          console.error(`Error processing booking ${booking.id}:`, error);
        }
      }
      handleRefetch();
    }
    setShowCancelModal(false);
    try {
      sessionStorage.removeItem("referrerTourBookingSuccess");
    } catch (_err) {
      // ignore
    }
  };

  return (
    <Modal open={showCancelModal} onClose={() => setShowCancelModal(false)}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: '10px',
          outline: 'none',
          maxWidth: '300px',
        }}
      >
        <Typography variant="h6" mb={2}>
          Cancel Booking
        </Typography>
        <Typography variant="body1" mb={2}>
          {isRecurring
            ? "This is a recurring booking. Would you like to cancel the entire series or just this instance?"
            : "Are you sure you want to cancel this booking?"}
        </Typography>
        {cancelPreviewItems.length > 0 ? (
          <Box
            sx={{
              mb: 2,
              px: 1.25,
              py: 1,
              borderRadius: "8px",
              backgroundColor: "rgba(15,23,42,0.04)",
              border: "1px solid rgba(15,23,42,0.08)",
            }}
          >
            {cancelPreviewItems.map((item) => (
              <Typography key={item.id || `${item.title}-${item.time}`} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{item.title}</strong>
                {item.time ? ` - ${item.time}` : ""}
                {item.invitee ? ` - ${item.invitee}` : ""}
              </Typography>
            ))}
            {normalizedCurrentBookings.length > cancelPreviewItems.length ? (
              <Typography variant="caption" color="text.secondary">
                +{normalizedCurrentBookings.length - cancelPreviewItems.length} more booking(s)
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {isRecurring && (
          <RadioGroup
            row
            value={cancelOption}
            onChange={(e) => setCancelOption(e.target.value)}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label="Cancel This Instance"
            />
            <FormControlLabel
              value="series"
              control={<Radio />}
              label="Cancel Entire Series"
            />
          </RadioGroup>
        )}
        {/* Display the cancellation message */}
        <Typography variant="caption" color="text.secondary" mb={2}>
          {cancellationMessage}
        </Typography>
        <OutlinedInput
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          sx={{ borderRadius: '10px', my: 2 }}
          placeholder="Add an optional message"
          multiline
          minRows={4}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
          <Button variant="outlined" onClick={() => setShowCancelModal(false)}>
            Keep Booking
          </Button>
          <Button variant="outlined" onClick={() => handleBookingCancellation(currentData, message)}>
            Confirm Cancellation
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CancelSchedule;
